import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";

const CommonUIContext = createContext();

export const useCommonUI = () => useContext(CommonUIContext);

export const CommonUIProvider = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  // Snackbar: a transient bottom toast with an optional action button (e.g.
  // the "Undo" after converting an action to a task). Auto-dismisses.
  const [snackbar, setSnackbar] = useState(null);
  const snackbarTimerRef = useRef(null);

  const openModal = (content) => {
    setModalContent(content);
    setIsVisible(true);
  };

  const closeModal = () => {
    setIsVisible(false);
    setModalContent(null);
  };

  const hideSnackbar = useCallback(() => {
    if (snackbarTimerRef.current) {
      clearTimeout(snackbarTimerRef.current);
      snackbarTimerRef.current = null;
    }
    setSnackbar(null);
  }, []);

  const showSnackbar = useCallback(
    ({ message, actionLabel, onAction, variant, duration = 5000 }) => {
      if (snackbarTimerRef.current) clearTimeout(snackbarTimerRef.current);
      setSnackbar({ message, actionLabel, onAction, variant });
      snackbarTimerRef.current = setTimeout(() => setSnackbar(null), duration);
    },
    []
  );

  const handleSnackbarAction = () => {
    if (snackbar?.onAction) snackbar.onAction();
    hideSnackbar();
  };

  return (
    <CommonUIContext.Provider
      value={{
        isVisible,
        modalContent,
        openModal,
        closeModal,
        showSnackbar,
        hideSnackbar,
      }}
    >
      {children}
      {isVisible && modalContent && (
        <div style={styles.modalBackground}>
          <div style={styles.modalContent}>
            <button
              style={styles.closeButton}
              onClick={closeModal}
              aria-label="Close"
            >
              ✕
            </button>
            {modalContent}
          </div>
        </div>
      )}
      {snackbar && (
        <div style={styles.snackbar}>
          {snackbar.variant === "success" && (
            <span style={styles.snackbarSuccessBadge}>✓</span>
          )}
          {snackbar.variant === "danger" && (
            <span style={styles.snackbarDangerBadge}>!</span>
          )}
          <span style={styles.snackbarMessage}>{snackbar.message}</span>
          {snackbar.actionLabel && (
            <button style={styles.snackbarAction} onClick={handleSnackbarAction}>
              {snackbar.actionLabel}
            </button>
          )}
        </div>
      )}
    </CommonUIContext.Provider>
  );
};

const styles = {
  modalBackground: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    // Above the fixed top bar (1100) and bottom bar so the modal is never
    // clipped behind app chrome.
    zIndex: 3000,
    padding: "16px",
    boxSizing: "border-box",
  },
  modalContent: {
    position: "relative",
    backgroundColor: "var(--modal-bg)",
    color: "var(--modal-text)",
    padding: "20px",
    paddingTop: "40px",
    borderRadius: "10px",
    width: "300px",
    // Tall dialogs (e.g. the action-template steps editor) scroll inside the
    // box instead of overflowing the viewport.
    maxHeight: "calc(100vh - 32px)",
    overflowY: "auto",
    boxSizing: "border-box",
  },
  closeButton: {
    position: "absolute",
    top: "8px",
    right: "8px",
    background: "none",
    border: "none",
    fontSize: "18px",
    lineHeight: 1,
    color: "#666",
    cursor: "pointer",
    padding: "6px",
  },
  snackbar: {
    position: "fixed",
    bottom: "24px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#333",
    color: "#fff",
    padding: "12px 16px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.3)",
    zIndex: 2000,
    maxWidth: "90%",
  },
  snackbarSuccessBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    backgroundColor: "#5FD788",
    color: "#fff",
    fontSize: "13px",
    fontWeight: "bold",
    flexShrink: 0,
  },
  snackbarDangerBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    backgroundColor: "#ff5a5a",
    color: "#fff",
    fontSize: "13px",
    fontWeight: "bold",
    flexShrink: 0,
  },
  snackbarMessage: {
    fontSize: "14px",
  },
  snackbarAction: {
    background: "none",
    border: "none",
    color: "#4da3ff",
    fontWeight: "bold",
    fontSize: "14px",
    textTransform: "uppercase",
    cursor: "pointer",
    flexShrink: 0,
  },
};
